import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClub, useEngineState, type KioskPerson } from '../club/ClubProvider.tsx'
import { Banner, Field, errorText } from '../ui/components.tsx'
import { PinPad } from './PinPad.tsx'

type Step =
  | { s: 'grid' }
  | { s: 'pin'; p: KioskPerson }
  | { s: 'change'; p: KioskPerson; phase: 'old' | 'new' | 'confirm'; old: string; first: string }
  | { s: 'reset'; p: KioskPerson; phase: 'code' | 'new' | 'confirm'; code: string; first: string }

const initials = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('')

/** The workstation while nobody is signed in: pick a name, enter the PIN (the SERVER checks it). First PIN and forgotten PIN use a one-time code
 *  from the head greenkeeper or the administrator. Nothing about anybody is kept on this screen after it closes. */
export function KioskLock() {
  const { t } = useTranslation()
  const { kiosk, org, site } = useClub()
  const online = useEngineState().online !== false
  const [people, setPeople] = useState<KioskPerson[] | null>(null)
  const [step, setStep] = useState<Step>({ s: 'grid' })
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ tone: 'error' | 'ok'; text: string } | null>(null)

  const load = () => { void kiosk!.roster().then(setPeople).catch(() => setPeople([])) }
  useEffect(() => { if (step.s === 'grid') { load() } }, [step.s]) // eslint-disable-line react-hooks/exhaustive-deps
  const reset = () => { setStep({ s: 'grid' }); setPin(''); setMsg(null) }
  const fail = (code: string, remaining?: number) => {
    setMsg({ tone: 'error', text: code === 'KIOSK_PIN_INVALID' && remaining !== undefined ? t('kiosk.wrongPin', { count: remaining }) : errorText(t, code) })
    setPin('')
  }

  async function submitPin(p: KioskPerson, value: string) {
    setBusy(true); setMsg(null)
    try {
      const r = await kiosk!.unlock(p.person_id, value)
      if (!r.ok) { if (r.code === 'KIOSK_PIN_NOT_SET') setStep({ s: 'reset', p, phase: 'code', code: '', first: '' }); else fail(r.code, r.remaining); return }
      // the session is open: the shell takes over
    } catch { setMsg({ tone: 'error', text: t('errors.network') }); setPin('') } finally { setBusy(false) }
  }
  async function savePin(p: KioskPerson, proof: { kind: 'pin' | 'code'; value: string }, next: string) {
    setBusy(true); setMsg(null)
    try {
      const r = await kiosk!.setPin(p.person_id, proof, next)
      if (!r.ok) { fail(r.code, r.remaining); setStep(proof.kind === 'pin' ? { s: 'change', p, phase: 'old', old: '', first: '' } : { s: 'reset', p, phase: 'code', code: '', first: '' }); return }
      await submitPin(p, next)
    } catch { setMsg({ tone: 'error', text: t('errors.network') }); setPin('') } finally { setBusy(false) }
  }

  // entering a PIN: six digits submit by themselves
  useEffect(() => {
    if (pin.length !== 6 || busy) return
    if (step.s === 'pin') void submitPin(step.p, pin)
    else if (step.s === 'change') {
      if (step.phase === 'old') { setStep({ ...step, phase: 'new', old: pin }); setPin('') }
      else if (step.phase === 'new') { setStep({ ...step, phase: 'confirm', first: pin }); setPin('') }
      else if (pin === step.first) void savePin(step.p, { kind: 'pin', value: step.old }, pin)
      else { setMsg({ tone: 'error', text: t('kiosk.pinMismatch') }); setPin(''); setStep({ ...step, phase: 'new', first: '' }) }
    } else if (step.s === 'reset' && step.phase !== 'code') {
      if (step.phase === 'new') { setStep({ ...step, phase: 'confirm', first: pin }); setPin('') }
      else if (pin === step.first) void savePin(step.p, { kind: 'code', value: step.code }, pin)
      else { setMsg({ tone: 'error', text: t('kiosk.pinMismatch') }); setPin(''); setStep({ ...step, phase: 'new', first: '' }) }
    }
  }, [pin]) // eslint-disable-line react-hooks/exhaustive-deps

  const back = <button className="btn" onClick={reset} data-testid="kiosk-back">{t('common.back')}</button>
  return (
    <main className="mx-auto flex min-h-dvh max-w-3xl flex-col gap-5 px-4 py-6" data-testid="kiosk-lock">
      <header>
        <p className="text-sm font-bold">{org.org_name} · {site.name}</p>
        <h1 className="text-3xl font-bold">{step.s === 'grid' ? t('kiosk.title') : step.p.display_name}</h1>
      </header>
      {!online && <Banner tone="warn">{t('kiosk.needsNetwork')}</Banner>}
      {msg && <Banner tone={msg.tone}><span data-testid="kiosk-message">{msg.text}</span></Banner>}

      {step.s === 'grid' && (
        <>
          <p className="opacity-80">{t('kiosk.pickHelp')}</p>
          {people === null ? <p>{t('common.loading')}</p> : people.length === 0 ? <p className="opacity-70">{t('kiosk.noPeople')}</p> : (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="kiosk-roster">
              {people.map((p) => (
                <li key={p.person_id}>
                  <button className="card flex min-h-24 w-full flex-col items-center justify-center gap-1 text-center" onClick={() => { setStep(p.has_pin ? { s: 'pin', p } : { s: 'reset', p, phase: 'code', code: '', first: '' }); setMsg(null) }} data-testid="kiosk-person">
                    <span className="grid size-12 place-items-center rounded-full bg-brand text-lg font-bold text-white" aria-hidden>{initials(p.display_name)}</span>
                    <span className="font-semibold">{p.display_name}</span>
                    {!p.has_pin && <span className="meta">{t('kiosk.noPin')}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {step.s === 'pin' && (
        <>
          <PinPad value={pin} onChange={setPin} disabled={busy || !online} label={t('kiosk.enterPin', { name: step.p.display_name })} />
          <p className="text-center text-sm font-semibold">{t('kiosk.enterPin', { name: step.p.display_name })}</p>
          <div className="mx-auto flex flex-wrap justify-center gap-3">
            <button className="btn !min-h-11" onClick={() => { setStep({ s: 'change', p: step.p, phase: 'old', old: '', first: '' }); setPin(''); setMsg(null) }} data-testid="kiosk-change">{t('kiosk.changePin')}</button>
            <button className="btn !min-h-11" onClick={() => { setStep({ s: 'reset', p: step.p, phase: 'code', code: '', first: '' }); setPin(''); setMsg(null) }} data-testid="kiosk-forgot">{t('kiosk.forgot')}</button>
            {back}
          </div>
        </>
      )}

      {step.s === 'change' && (
        <>
          <p className="text-center text-lg font-semibold">{t(step.phase === 'old' ? 'kiosk.oldPin' : step.phase === 'new' ? 'kiosk.newPin' : 'kiosk.confirmPin')}</p>
          <PinPad value={pin} onChange={setPin} disabled={busy || !online} label={t('kiosk.newPin')} />
          <div className="mx-auto">{back}</div>
        </>
      )}

      {step.s === 'reset' && (
        step.phase === 'code' ? (
          <form className="mx-auto flex w-full max-w-sm flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (/^\d{8}$/.test(step.code)) { setStep({ ...step, phase: 'new' }); setMsg(null) } }}>
            <p>{t('kiosk.resetHelp')}</p>
            <Field label={t('kiosk.resetCode')} htmlFor="reset-code">
              <input id="reset-code" className="input text-center text-2xl tracking-widest" inputMode="numeric" autoComplete="one-time-code" maxLength={8} value={step.code}
                onChange={(e) => setStep({ ...step, code: e.target.value.replace(/\D/g, '') })} data-testid="kiosk-reset-code" />
            </Field>
            <button className="btn btn-primary" disabled={!/^\d{8}$/.test(step.code)} data-testid="kiosk-reset-next">{t('common.next')}</button>
            {back}
          </form>
        ) : (
          <>
            <p className="text-center text-lg font-semibold">{t(step.phase === 'new' ? 'kiosk.newPin' : 'kiosk.confirmPin')}</p>
            <PinPad value={pin} onChange={setPin} disabled={busy || !online} label={t('kiosk.newPin')} />
            <div className="mx-auto">{back}</div>
          </>
        )
      )}
    </main>
  )
}
